// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { OrderSyncSignal } from '@/components/order-sync-signal';
import { resyncReturnOrder } from '@/return-order/api/resync-return-order';
import { isCancelled, isReceived } from '@/return-order/constants/status';
import { type ROStatus } from '@/return-order/constants/status';
import {
  returnOrderDetailQueryKey,
  returnOrdersQueryKey,
} from '@/return-order/query-keys';
import { DateTime } from 'luxon';

interface Props {
  returnOrderKey: string;
  // The RO number, used to estimate resync time from its shipments
  returnOrderNumber: string;
  status: ROStatus;
  // Hardfin's last poll of the 3PL for this return order
  lastSyncedAt: DateTime | null;
  // The 3PL's last reported data update for this return order
  lastThirdPartyUpdateAt: DateTime | null;
  // Whether the 3PL binding is currently in an error state
  hasSyncError: boolean;
  // The latest 3PL sync error text (empty when there is none)
  syncErrorMessage: string;
}

/**
 * 3PL sync-status signal for a return order: the shared OrderSyncSignal wired
 * to the return order resync API and query keys. The order is terminal once
 * received or cancelled, and "Sent to 3PL" applies while it awaits a receive.
 */
export function ReturnOrderSyncSignal(props: Props) {
  // The order is terminal once received or cancelled; the icon greys and the
  // "next check" line is hidden.
  const isTerminal = isReceived(props.status) || isCancelled(props.status);
  // "Sent to 3PL" applies only while the order is still awaiting a receive; once
  // receiving activity begins (or the order becomes terminal) it reads
  // "Synced from 3PL".
  const isAwaiting =
    !isTerminal &&
    (props.status === 'AWAITING_3PL' || props.status.startsWith('OPEN_'));

  return (
    <OrderSyncSignal
      orderType="return"
      orderKey={props.returnOrderKey}
      orderNumber={props.returnOrderNumber}
      isTerminal={isTerminal}
      isAwaiting={isAwaiting}
      lastSyncedAt={props.lastSyncedAt}
      lastThirdPartyUpdateAt={props.lastThirdPartyUpdateAt}
      hasSyncError={props.hasSyncError}
      syncErrorMessage={props.syncErrorMessage}
      resyncOrder={resyncReturnOrder}
      detailQueryKey={returnOrderDetailQueryKey(props.returnOrderKey)}
      listQueryKey={returnOrdersQueryKey}
    />
  );
}
